import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { limit, query } from "firebase/firestore";
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { LICENSE_TYPES } from "../../config";
import { useSession } from "../../auth/AuthProvider";
import {
  LICENSE_CSV_COLUMNS,
  STATUS_META,
  STATUS_ORDER,
  compareBy,
  countByStatus,
  downloadCsv,
  isDevelopment,
  licenseStatus,
  toCsv,
  todayStamp,
} from "../../lib/licenses";
import {
  LICENCE_SORTS,
  SEARCH_LIMIT,
  developmentCountQuery,
  licenceCountQuery,
  licenceListQuery,
  licenceSearchQuery,
} from "../../lib/queries";
import { matchesAllWords, primaryToken, searchWords } from "../../lib/search";
import {
  describeQueryError,
  fetchAll,
  useCounts,
  usePagedQuery,
} from "../../hooks/usePaged";
import { useLiveQuery } from "../../hooks/useLive";
import {
  ConfirmDialog,
  Loading,
  PageHeader,
  StatCard,
  StatGrid,
} from "../../components/ui";
import { setDevelopment } from "../../lib/operations";
import LicenseTable, { pagingProps } from "../../components/LicenseTable";
import LicenseDialog from "../../components/LicenseDialog";
import GenerateKeysDialog from "./GenerateKeysDialog";
import { useAdminData } from "./AdminData";

const PAGE_SIZE = 25;
const COLUMNS = [
  "key",
  "status",
  "type",
  "distributor",
  "customer",
  "created",
  "expiry",
];

// The same filters, applied in the browser to search results.
function matchesFilters(license, { includeDev, status, type, distributor }) {
  if (!includeDev && isDevelopment(license)) return false;
  if (status !== "all" && licenseStatus(license) !== status) return false;
  if (type !== "all" && license.type !== type) return false;
  if (distributor === "stock") return !license.distributorId;
  if (distributor !== "all") return license.distributorId === distributor;
  return true;
}

function useDebounced(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

// Browsing reads one page at a time, sorted and filtered by Firestore, with
// the status tiles counted by Firestore. Typing a search switches to the
// first SEARCH_LIMIT keys matching the words, filtered in the browser.
export default function LicensesPage({ product }) {
  const session = useSession();
  const { distributors } = useAdminData();
  const [params, setParams] = useSearchParams();
  const status = params.get("status") || "all";
  const [search, setSearch] = useState("");
  const [type, setType] = useState("all");
  const [distributor, setDistributor] = useState("all");
  const [sort, setSort] = useState("newest");
  // Development keys are left out of the list and the counts unless shown.
  const [includeDev, setIncludeDev] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [bulk, setBulk] = useState(null); // null | { development, ids }
  const [bulkBusy, setBulkBusy] = useState(false);
  const [exporting, setExporting] = useState(null); // null | rows fetched so far
  const [refresh, setRefresh] = useState(0);

  const setStatus = (value) =>
    setParams(value === "all" ? {} : { status: value }, { replace: true });

  const debounced = useDebounced(search);
  const words = searchWords(debounced);
  const token = primaryToken(words);
  const searching = !!token;
  const filters = { includeDev, status, type, distributor };
  const filterKey = `${product.id}|${includeDev}|${type}|${distributor}`;

  // --- Browse mode
  const paged = usePagedQuery(
    searching ? null : `${filterKey}|${status}|${sort}`,
    () => licenceListQuery(product.id, filters, sort),
    { pageSize: PAGE_SIZE, extra: { product: product.id } },
  );
  const countDefs = Object.fromEntries(
    ["all", ...STATUS_ORDER].map((s) => [
      s,
      () => licenceCountQuery(product.id, { ...filters, status: s }),
    ]),
  );
  countDefs.development = () => developmentCountQuery(product.id);
  // Recount when the visible page changes (e.g. after an edit) or on request.
  const counted = useCounts(
    searching
      ? null
      : `${filterKey}|${refresh}|${paged.rows.map((r) => r.displayStatus).join()}`,
    countDefs,
  );

  // --- Search mode
  const found = useLiveQuery(
    searching ? `${product.id}|search|${token}` : null,
    () => query(licenceSearchQuery(product.id, token), limit(SEARCH_LIMIT)),
    { product: product.id },
  );
  const searchScope = useMemo(
    () =>
      found.data.filter(
        (l) =>
          matchesAllWords(l, words) &&
          matchesFilters(l, { ...filters, status: "all" }),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [found.data, words.join(), includeDev, type, distributor],
  );
  const searchRows = useMemo(
    () =>
      searchScope
        .filter((l) => status === "all" || licenseStatus(l) === status)
        .sort(sort === "expiry" ? compareBy("expirySort", "asc") : () => 0),
    [searchScope, status, sort],
  );

  const rows = searching ? searchRows : paged.rows;
  const loading = searching ? found.loading : paged.loading;
  const error = searching ? found.error : paged.error || counted.error;
  const counts = searching
    ? { all: searchScope.length, ...countByStatus(searchScope) }
    : counted.counts;
  const openLicense = openId ? rows.find((l) => l.id === openId) : null;

  // Bulk development marking applies to search results, e.g. "wing" — only
  // once the results are for exactly what's typed, never stale ones.
  const settled = searching && debounced === search && !found.loading;
  const toMark = settled ? rows.filter((l) => !isDevelopment(l)) : [];
  const toUnmark = settled && includeDev ? rows.filter(isDevelopment) : [];
  const bulkFor = (development, list) => ({
    development,
    term: search.trim(),
    ids: list.map((l) => l.id),
    names: [...new Set(list.map((l) => l.customer || l.licenseKey))],
  });

  async function runBulk() {
    setBulkBusy(true);
    try {
      await setDevelopment({
        productId: product.id,
        licenseIds: bulk.ids,
        development: bulk.development,
        actor: session.email,
      });
      setRefresh((n) => n + 1);
    } catch (e) {
      console.error(e);
    } finally {
      setBulk(null);
      setBulkBusy(false);
    }
  }

  async function exportRows() {
    setExporting(0);
    try {
      const all = searching
        ? searchRows
        : await fetchAll(licenceListQuery(product.id, filters, sort), {
            extra: { product: product.id },
            onProgress: setExporting,
          });
      downloadCsv(
        `${product.path}-keys-${todayStamp()}.csv`,
        toCsv(all, LICENSE_CSV_COLUMNS),
      );
    } catch (e) {
      console.error(e);
    } finally {
      setExporting(null);
    }
  }

  const tileValue = (name) =>
    counted.loading && !searching ? "…" : (counts[name] ?? 0).toLocaleString();

  return (
    <>
      <PageHeader
        eyebrow="Licences"
        title={product.name}
        subtitle={product.description}
        actions={
          <>
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<DownloadIcon />}
              onClick={exportRows}
              disabled={exporting !== null || !rows.length}
              sx={{ borderColor: "divider" }}
            >
              {exporting !== null
                ? `Exporting ${exporting.toLocaleString()}…`
                : "Export"}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setGenerating(true)}
            >
              Generate keys
            </Button>
          </>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {describeQueryError(error)}
        </Alert>
      )}

      <StatGrid>
        <StatCard
          label={searching ? "All matches" : "All keys"}
          value={tileValue("all")}
          active={status === "all"}
          onClick={() => setStatus("all")}
        />
        {STATUS_ORDER.map((s) => (
          <StatCard
            key={s}
            label={STATUS_META[s].label}
            value={tileValue(s)}
            status={s}
            active={status === s}
            onClick={() => setStatus(status === s ? "all" : s)}
          />
        ))}
      </StatGrid>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, my: 2.5 }}>
        <TextField
          placeholder="Search key, customer, location, remarks…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: "1 1 280px" }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
        <TextField
          select
          label="Type"
          value={type}
          onChange={(e) => setType(e.target.value)}
          sx={{ minWidth: 130 }}
        >
          <MenuItem value="all">All types</MenuItem>
          {LICENSE_TYPES.map((t) => (
            <MenuItem key={t} value={t}>
              {t}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Distributor"
          value={distributor}
          onChange={(e) => setDistributor(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="all">All distributors</MenuItem>
          <MenuItem value="stock">LiFE stock & direct</MenuItem>
          {distributors.map((d) => (
            <MenuItem key={d.id} value={d.id}>
              {d.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Sort"
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          sx={{ minWidth: 150 }}
        >
          {Object.entries(LICENCE_SORTS).map(([value, { label }]) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </TextField>
        <FormControlLabel
          control={
            <Switch
              checked={includeDev}
              onChange={(e) => setIncludeDev(e.target.checked)}
            />
          }
          label={`Include development keys${counted.counts.development ? ` (${counted.counts.development})` : ""}`}
          sx={{ ml: 0 }}
        />
      </Box>

      {searching && found.data.length >= SEARCH_LIMIT && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Showing the newest {SEARCH_LIMIT} keys that match. Add another word to
          narrow the search.
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1,
          mb: 1,
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mr: "auto" }}>
          {searching
            ? `${rows.length} matching ${rows.length === 1 ? "key" : "keys"}`
            : counts[status] !== undefined
              ? `${counts[status].toLocaleString()} keys`
              : " "}
        </Typography>
        {toMark.length > 0 && (
          <Button size="small" onClick={() => setBulk(bulkFor(true, toMark))}>
            Mark {toMark.length} as development
          </Button>
        )}
        {toUnmark.length > 0 && (
          <Button
            size="small"
            color="inherit"
            onClick={() => setBulk(bulkFor(false, toUnmark))}
          >
            Unmark {toUnmark.length} development
          </Button>
        )}
      </Box>

      {loading && !rows.length ? (
        <Loading label={`Loading ${product.name} keys…`} />
      ) : (
        <LicenseTable
          rows={rows}
          columns={COLUMNS}
          onRowClick={(l) => setOpenId(l.id)}
          resetKey={`${filterKey}|${status}|${sort}|${token}`}
          paging={
            searching
              ? undefined
              : pagingProps(paged, PAGE_SIZE, counts[status])
          }
          emptyText={
            searching
              ? "No keys match this search."
              : "No keys match these filters."
          }
        />
      )}

      {openLicense && (
        <LicenseDialog
          license={openLicense}
          canManage
          actor={session.email}
          onClose={() => {
            setOpenId(null);
            setRefresh((n) => n + 1);
          }}
        />
      )}
      <ConfirmDialog
        open={!!bulk}
        title={
          bulk?.development
            ? "Mark as development keys?"
            : "Unmark development keys?"
        }
        body={
          bulk && (
            <>
              {bulk.development
                ? `The ${bulk.ids.length} keys matching "${bulk.term}" will be left out of counts and trends. They keep working as normal.`
                : `The ${bulk.ids.length} development keys matching "${bulk.term}" will be counted again.`}
              <Box component="span" sx={{ display: "block", mt: 1 }}>
                Customers: {bulk.names.slice(0, 8).join(", ")}
                {bulk.names.length > 8
                  ? `, and ${bulk.names.length - 8} more`
                  : ""}
              </Box>
            </>
          )
        }
        confirmLabel={bulk?.development ? "Mark as development" : "Unmark"}
        busy={bulkBusy}
        onConfirm={runBulk}
        onClose={() => setBulk(null)}
      />
      <GenerateKeysDialog
        open={generating}
        product={product}
        onClose={() => {
          setGenerating(false);
          setRefresh((n) => n + 1);
        }}
      />
    </>
  );
}
